import { Select, TextField } from '@cimpress/react-components';
import { useContext, useEffect, useMemo, useState } from 'react';
import { SketchPicker } from 'react-color';
import { ApiKeyContext } from '../App';
import { getAdaptToProductUrl } from '../api/urlHelpers';
import { getPhotoSceneUrl } from '../api';
import { getProductNameBasedOnSku } from '../api';
import {
    CardBuilder,
    Control,
    Culture,
    CustomSelector,
    Page,
    PreviewRow,
    PreviewRows,
    PreviewSection,
    SurfaceSelector,
} from '../Components';
import { useSampleDocuments } from '../Components/common/useSampleDocuments';
import { useAdaptToProduct } from '../Components/AdaptToProduct/useAdaptToProduct';
import { useStorageDocumentUrl } from '../Components/AdaptToProduct/useStorageDocumentUrl';
import {
    getProductNameForSourceDocument,
    getSourceDocumentOptions,
} from '../Components/AdaptToProduct/sourceDocumentOptions';
import { isUrl } from '../tools';
import { getUrl } from '../tools/urlHelper';
import prodsAndOptions from '../data/prodandoptions.json';
import targetSurfacesData from '../data/adaptToProduct/targetSurfaces.json';
import templateMatchingTestCasesData from '../data/adaptToProduct/templateMatchingTestCases.json';
import { useCancelableEffect } from '../tools';

const CULTURE_VALUE_OPTIONS = Object.values(Culture).map((culture) => {
    return { label: culture, value: culture };
});

const SKU_REGEX = new RegExp(/^[A-Z]{3,3}-[A-Z0-9]+$/g);

function findFullfillmentInfoForProductKey(key?: string) {
    if (!key) {
        return undefined;
    }
    const match = prodsAndOptions.find((x) => x.PRODUCT_KEY === key);
    return match ? match.FULFILLMENT_SKU : undefined;
}

// Extracts the SKU-like part of the URL
function getSkuFromSurfaceSpecUrl(url: string) {
    const surfaceSpecParts = url.split('?')[0].split('/');
    return surfaceSpecParts.find((x) => SKU_REGEX.test(x))!;
}

export const AdaptToProduct = () => {
    const PRODUCT_OPTIONS = [
        { label: 'Business Cards', value: 'businessCards' },
        { label: 'Flyers', value: 'flyers' },
        { label: 'Car Magnets', value: 'carMagnets' },
        { label: 'Decals', value: 'decals' },
        { label: 'Bags', value: 'bags' },
        { label: 'T-Shirts', value: 'tShirts' },
        { label: 'Mugs', value: 'mugs' },
        { label: 'Caps', value: 'caps' },
        { label: 'Notebooks', value: 'notebooks' },
        { label: 'Stamps', value: 'stamps' },
    ];

    const {
        sourceProductName,
        sourceDocumentUrl,
        sourceSurfaceSpecificationUrl,
        targetProductName,
        targetSurfaceSpecificationUrl,
        targetSubstrateColor,
        culture,
        setSourceProductName,
        setSourceDocumentUrl,
        setSourceSurfaceSpecificationUrl,
        setTargetProductName,
        setTargetSurfaceSpecificationUrl,
        setTargetSubstrateColor,
        setCulture,
    } = useAdaptToProduct();

    const apiKey = useContext(ApiKeyContext);

    const sourceTestCase = templateMatchingTestCasesData.find(
        (testCase) => testCase.sourceDocumentUrl === sourceDocumentUrl,
    );
    const targetSurfaceOptions = (sourceTestCase?.targets ?? targetSurfacesData).map((surface) => ({
        label: surface.label,
        value: surface.value,
    }));

    const [sourceRow, setSourceRow] = useState(PreviewRow());
    const [resultRow, setResultRow] = useState(PreviewRow());
    const [hasPageLoaded, setHasPageLoaded] = useState(false);

    const sampleDocuments = useSampleDocuments('adaptToProduct_source_documents.json');
    const predefinedDocuments = useMemo(
        () => getSourceDocumentOptions(sampleDocuments, sourceProductName),
        [sampleDocuments, sourceProductName],
    );

    const { url: storageSourceDocumentUrl } = useStorageDocumentUrl(sourceDocumentUrl);

    const hasRequiredInput =
        sourceProductName && storageSourceDocumentUrl && targetProductName && targetSurfaceSpecificationUrl;

    useEffect(() => {
        if (sourceDocumentUrl) {
            if (sourceSurfaceSpecificationUrl) {
                const sourceCard = CardBuilder.ofType('documentUrl')
                    .withTitle('Source Document')
                    .withLoader(async ({ signal }) => {
                        const photoSceneUrl = (await getPhotoSceneUrl(sourceSurfaceSpecificationUrl, signal)) ?? '';

                        return {
                            documentUrl: sourceDocumentUrl,
                            photoSceneUrl,
                            surfaceUrl: sourceSurfaceSpecificationUrl,
                        };
                    })
                    .build();
                setSourceRow(PreviewRow(sourceCard));
            } else {
                const sourceCard = CardBuilder.ofType('documentUrl')
                    .withTitle('Source Document')
                    .withData({
                        documentUrl: sourceDocumentUrl,
                    })
                    .build();
                setSourceRow(PreviewRow(sourceCard));
            }
        } else {
            setSourceRow(PreviewRow());
        }
    }, [sourceDocumentUrl, sourceProductName, sourceSurfaceSpecificationUrl]);

    useEffect(() => {
        if (!hasRequiredInput) {
            setResultRow(PreviewRow());
            return;
        }

        const resultCard = CardBuilder.ofType('documentUrl')
            .withTitle(`Adapted Document - ${targetProductName}`)
            .withLoader(async ({ signal }) => {
                const photoSceneUrl = (await getPhotoSceneUrl(targetSurfaceSpecificationUrl, signal)) ?? '';

                const adaptToProductUrl = getAdaptToProductUrl(
                    sourceProductName,
                    storageSourceDocumentUrl,
                    targetProductName,
                    targetSurfaceSpecificationUrl,
                    apiKey,
                    sourceSurfaceSpecificationUrl || undefined,
                    targetSubstrateColor && targetSubstrateColor !== 'rgb(255, 255, 255)'
                        ? targetSubstrateColor
                        : undefined,
                    culture && culture !== 'en-us' ? culture : undefined,
                    undefined,
                    true,
                );

                return {
                    documentUrl: adaptToProductUrl ?? '',
                    photoSceneUrl,
                    surfaceUrl: targetSurfaceSpecificationUrl,
                    backgroundColor:
                        targetSubstrateColor && targetSubstrateColor !== 'rgb(255, 255, 255)'
                            ? targetSubstrateColor.substring(1)
                            : 'FFFFFF',
                };
            })
            .build();

        setResultRow(PreviewRow(resultCard));
    }, [
        hasRequiredInput,
        sourceProductName,
        storageSourceDocumentUrl,
        sourceSurfaceSpecificationUrl,
        targetProductName,
        targetSurfaceSpecificationUrl,
        targetSubstrateColor,
        culture,
        apiKey,
    ]);

    // Auto-detect target product name from target surface spec URL
    useCancelableEffect(
        (signal) => {
            (async () => {
                if (targetSurfaceSpecificationUrl) {
                    const extractedProductSku = getSkuFromSurfaceSpecUrl(targetSurfaceSpecificationUrl);
                    const productSku = findFullfillmentInfoForProductKey(extractedProductSku) || extractedProductSku;
                    const productName = (await getProductNameBasedOnSku(productSku, signal)) ?? '';
                    handleTargetProductNameChange(productName);
                }
            })();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [targetSurfaceSpecificationUrl],
    );

    useEffect(() => {
        setHasPageLoaded(true);
    }, []);

    function handleTargetProductNameChange(productName: string) {
        // We are not calling this setTargetProductName for the first time when page loads because we want to keep value in url params.
        // If we call this when page loads this will override value in URL param.
        if (hasPageLoaded) {
            setTargetProductName(productName);
        }
    }

    const setSourceDocumentUrlHandler = (docUrl: string) => {
        setSourceDocumentUrl(getUrl(docUrl) ?? docUrl);

        const productName = getProductNameForSourceDocument(docUrl, sampleDocuments);
        if (productName) {
            setSourceProductName(productName);
        }
    };

    const onChangeCulture = (selected: { label: string; value: string } | null) => {
        if (selected) {
            setCulture(selected.value);
        }
    };

    return (
        <Page>
            <Page.Title>Adapt to Product</Page.Title>
            <Page.Controls>
                <Control>
                    <Control.Title>Source Product Name</Control.Title>
                    <CustomSelector
                        selectorLabel="Source Product"
                        textfieldLabel="Source Product Name"
                        data={PRODUCT_OPTIONS}
                        value={sourceProductName}
                        setValue={setSourceProductName}
                        validator={() => true}
                    />
                </Control>

                <Control>
                    <Control.Title>Source Document </Control.Title>
                    <CustomSelector
                        key={sourceProductName}
                        selectorLabel="Document"
                        textfieldLabel="Document URL"
                        data={predefinedDocuments}
                        value={sourceDocumentUrl}
                        setValue={setSourceDocumentUrlHandler}
                        validator={isUrl}
                    />
                </Control>

                <Control>
                    <Control.Title>Source Surface Specification </Control.Title>
                    <SurfaceSelector
                        surfaceSpecificationUrl={sourceSurfaceSpecificationUrl}
                        setSurfaceSpecificationUrl={setSourceSurfaceSpecificationUrl}
                    />
                </Control>

                <Control>
                    <Control.Title>Target Product Name</Control.Title>
                    <TextField
                        label="Target Product Name"
                        value={targetProductName}
                        onChange={(e) => setTargetProductName(e.target.value)}
                    />
                </Control>

                <Control>
                    <Control.Title>Target Surface Specification</Control.Title>
                    <CustomSelector
                        selectorLabel="Target Surface"
                        textfieldLabel="Target Surface Specification URL"
                        key={sourceTestCase?.conceptId ?? 'default'}
                        data={targetSurfaceOptions}
                        value={targetSurfaceSpecificationUrl}
                        setValue={setTargetSurfaceSpecificationUrl}
                        validator={isUrl}
                    />
                </Control>

                <Control>
                    <Control.Title>Target Substrate Color </Control.Title>
                    <SketchPicker
                        width="65%"
                        color={targetSubstrateColor}
                        onChangeComplete={(e) => setTargetSubstrateColor(e.hex)}
                    />
                </Control>

                <Control>
                    <Control.Title>Culture </Control.Title>
                    <Select
                        onChange={onChangeCulture}
                        value={CULTURE_VALUE_OPTIONS.find((option) => option.value === culture)}
                        options={CULTURE_VALUE_OPTIONS}
                        label="Culture"
                    />
                </Control>
            </Page.Controls>

            <Page.Content>
                <PreviewSection
                    introduction="This page tests the Adapt to Product endpoint. Provide the required parameters to see how a document adapts from one product to another."
                    showIntro={!hasRequiredInput}
                    rows={PreviewRows(sourceRow, resultRow)}
                />
            </Page.Content>
        </Page>
    );
};

export default AdaptToProduct;
